import React from 'react'

import { cn } from '@/lib/utils'

interface TwinsMarkLoaderProps {
  className?: string
  dataTestId?: string
}

/**
 * TwinsMarkLoader — the Quick Study "thinking" mark. The twins glyph (two mirrored
 * hooks) replaces the generic Loader2 spinner everywhere Quick Study waits: the two
 * halves trade pulse beats, the mark solidifies, then eases through a settle
 * half-turn — the glyph is 180°-rotationally symmetric, so the turn lands exactly on
 * itself and the loop is seamless. Keyframes live in globals.css (`.twins-mark-loader`),
 * including the prefers-reduced-motion fallback to the static mark.
 *
 * Draws in `currentColor`: size and color come from the caller's className
 * (e.g. `h-4 w-4 text-twin-chat-fg-muted`), exactly like a lucide icon.
 */
export function TwinsMarkLoader(props: TwinsMarkLoaderProps) {
  const { className, dataTestId } = props

  return (
    <svg
      viewBox="0 0 16 16"
      fill="currentColor"
      aria-hidden="true"
      data-testid={dataTestId}
      className={cn('twins-mark-loader h-4 w-4 shrink-0 overflow-visible', className)}
    >
      <g className="tml-rot">
        <g className="tml-half-a">
          <path d="M8.29883 3.07666C8.49372 3.07666 8.65171 3.23465 8.65171 3.42954L8.65171 6.98305C8.65171 7.17794 8.8097 7.33593 9.00459 7.33593L12.409 7.33593C12.6039 7.33593 12.7619 7.49392 12.7619 7.68881L12.7619 8.33037C12.7619 8.52526 12.6039 8.68325 12.409 8.68325L9.00471 8.68325C8.80982 8.68325 8.65183 8.52526 8.65183 8.33037L8.65183 7.68874C8.65183 7.49385 8.49384 7.33586 8.29894 7.33586L7.70445 7.33586C7.50955 7.33586 7.35156 7.17787 7.35156 6.98298L7.35156 3.42954C7.35156 3.23465 7.50955 3.07666 7.70445 3.07666L8.29883 3.07666Z" />
          <path d="M10.5952 5.7692C10.7305 5.90944 10.7305 6.13682 10.5952 6.27707L9.35668 7.56053C9.22134 7.70077 9.00193 7.70077 8.86659 7.56053L8.43733 7.11569C8.302 6.97545 8.302 6.74807 8.43733 6.60783L9.67586 5.32437C9.81119 5.18412 10.0306 5.18412 10.1659 5.32437L10.5952 5.7692Z" />
        </g>
        <g className="tml-half-b">
          <path d="M7.70508 12.9426C7.51019 12.9426 7.35219 12.7846 7.35219 12.5897L7.35219 9.03624C7.35219 8.84135 7.1942 8.68336 6.99931 8.68336L3.59493 8.68336C3.40004 8.68336 3.24204 8.52536 3.24204 8.33047L3.24205 7.68892C3.24205 7.49403 3.40004 7.33604 3.59493 7.33604L6.9992 7.33604C7.19409 7.33604 7.35208 7.49403 7.35208 7.68892L7.35208 8.33054C7.35208 8.52544 7.51007 8.68343 7.70496 8.68343L8.29946 8.68343C8.49435 8.68343 8.65234 8.84142 8.65234 9.03631L8.65234 12.5897C8.65234 12.7846 8.49435 12.9426 8.29946 12.9426L7.70508 12.9426Z" />
          <path d="M5.4048 10.2501C5.26947 10.1098 5.26947 9.88246 5.4048 9.74222L6.64332 8.45876C6.77866 8.31852 6.99807 8.31852 7.13341 8.45876L7.56267 8.9036C7.698 9.04384 7.698 9.27122 7.56267 9.41146L6.32414 10.6949C6.18881 10.8352 5.96939 10.8352 5.83406 10.6949L5.4048 10.2501Z" />
        </g>
      </g>
    </svg>
  )
}
